import styled from '@emotion/styled';
import { Grid, Paper, Typography } from '@material-ui/core';
import { createElement, Fragment, useCallback, useContext, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';

import ProductPricing from '../../../../../entity/ProductPricing';
import { MatchingFormat } from '../../../../../generated/graphql';
import { useGetIsPC } from '../../../../../utilityHooks/useGetIsPC';
import { AvailableDeliveryDueDateCalendar } from '../../../../component/compound/AvailableDeliveryDueDateCalendar';
import InputtedProjectPointTotal from '../../../../component/compound/InputtedProjectPointTotal';
import Spinner from '../../../../component/compound/Spinner';
import { SCREEN_LARGE } from '../../../../constant/pageWidth';
import { SPACING_HUGE, SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../../constant/spacing';
import { context as StateContext } from '../../../../context/state';
import mq from '../../../../styleUtility/mediaquery';
import { editingProjectDeliveryDueDatesAtom, editingProjectStateAtom } from '../../atoms/editingProjectStateAtom';
import { isProjectFlatAtom } from '../../atoms/isProjectFlatAtom';
import { projectAtom, projectLoadingAtom } from '../../atoms/projectAtom';
import { isPointRequired as getIsPointRequired } from '../../domain/helperFunctions';
import { useEditingProjectState } from '../../hooks/useEditingProjectState';
import useUpdateProject from '../../hooks/useUpdateProject';
import LeftWorkflowStepper from '../LeftWorkflowStepper';
import MobileOnlyWorkflowStepper from '../MobileOnlyWorkflowStepper';
import DueActions from './DueActions';
import DueFormatsRadioGroup from './DueFormatsRadioGroup';
import HelpExpressDelivery from './HelpExpressDelivery';

const DueScreen = () => {
  const isPC = useGetIsPC();
  const isFlat = useRecoilValue(isProjectFlatAtom);
  const isLoading = useRecoilValue(projectLoadingAtom);
  const project = useRecoilValue(projectAtom);
  const state = useRecoilValue(editingProjectStateAtom);
  const { updateDraftProjectAndGoToDifferentTab, updateDraftProjectAndGoToProjectsList } = useUpdateProject();

  const { acct, language, lastAccessedOrganization } = useContext(StateContext);
  const { updateCreationSetDeliveryDueDays, updateProjectMatchingFormat } = useEditingProjectState();

  const availableDeliveryDueDates = useRecoilValue(editingProjectDeliveryDueDatesAtom);
  const availableMatchingFormats = project?.projectType.matchingFormatGroup?.availableFormats;

  const [selectableFormats, matchingFormatErrors, formatUnchangeable] = useMemo(() => {
    let selectables = ['auto'] as Array<MatchingFormat>;
    const errors = [];

    if (!isLoading && state.creationSet && availableMatchingFormats) {
      selectables = project.projectType.matchingFormatGroup.availableFormats;
      // 特急チェック
      const expressDueDays = state.creationSet?.expressDueDays;
      if (expressDueDays && expressDueDays >= 1 && selectables.some((s) => s == 'competition'))
        errors.push('has_express_option');
      // original数チェック
      const originalAmount = state.creationSet?.creativeFormats.filter((cf) => cf.resizeBaseId == null).length;
      if (originalAmount && originalAmount >= 2 && selectables.some((s) => s == 'competition'))
        errors.push('has_many_originals');

      // errorsの中身がある場合はコンペを選択肢から外す
      if (errors.length >= 1) selectables = selectables.filter((s) => s != 'competition');
    }
    return [selectables, errors, selectables.length <= 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, state.creationSet?.expressDueDays]);

  // selectableFormatsの中に選択中のmatchingFormatがない場合にmatchingFormatを更新
  useEffect(() => {
    if (!isLoading && state.matchingFormat) {
      const selected = state.matchingFormat;
      if (selectableFormats.every((s) => s != selected)) {
        // 現状は auto と competition しかないのでとりあえず先頭(auto)にする
        updateProjectMatchingFormat(selectableFormats[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableFormats]);

  const onChangeDeliveryDueDays = useCallback(
    (days: number | null, expressDueDays: number) => {
      // 全CreationSetに対して同じ処理を実行する（v1プロジェクト対策）
      // この処理により個別のCreationSetだけ納品日を変更することは不可能
      updateCreationSetDeliveryDueDays(days, expressDueDays);
    },
    [updateCreationSetDeliveryDueDays],
  );

  const isPointRequired = getIsPointRequired(lastAccessedOrganization);
  const productPricing: ProductPricing | undefined = acct.productPricing || undefined;

  const renderAvailableDeliveryDueDateCalendar = useMemo(() => {
    if (availableDeliveryDueDates && state.creationSet?.deliveryDueDays) {
      return (
        <AvailableDeliveryDueDateCalendar
          availableDeliveryDueDates={availableDeliveryDueDates}
          selectDate={onChangeDeliveryDueDays}
          isPointRequired={isPointRequired}
          productPricing={productPricing}
          deliveryDueDays={state.creationSet.deliveryDueDays}
        />
      );
    }

    return null;
  }, [
    availableDeliveryDueDates,
    state.creationSet?.deliveryDueDays,
    onChangeDeliveryDueDays,
    isPointRequired,
    productPricing,
  ]);

  // if (!isFlat && (isLoading || !state)) return <Spinner fullHeight />;

  return (
    <Root isFlat={isFlat}>
      <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={isPC ? 4 : 2}>
        {/* don't show on flat */}
        {!isFlat ? (
          <LeftWorkflowStepper
            step="due"
            updateDraftAndGoToTargetStep={(targetStep) =>
              updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'due', targetStep })
            }
          />
        ) : (
          <Grid item xs={12} sm={12} md={12} lg={3} />
        )}
        <Grid item xs={12} sm={12} md={12} lg={9}>
          {/* Inputted Project Total Point */}
          {!isFlat && <InputtedProjectPointTotal step="due" />}

          {/* Due Date */}
          <Heading variant="h5">
            <FormattedMessage id="project_editing_page.due_setting.title" />
          </Heading>
          <Container>
            {isLoading || !state ? (
              <Spinner />
            ) : availableDeliveryDueDates && availableDeliveryDueDates.length ? (
              <>
                {renderAvailableDeliveryDueDateCalendar}
                {/* 納品日時・特急料金 のヘルプを表示 */}
                {availableDeliveryDueDates.length && isPointRequired && language === 'ja-JP' ? (
                  <HelpExpressDelivery
                    messageId="help.creation_list_delivery_due_days"
                    anchor={<FormattedMessage id="creation_set_list.express_delivery.help" />}
                  />
                ) : null}
              </>
            ) : (
              <Typography variant="body2">
                <FormattedMessage id="project_editing_page.due_setting.no_item" />
              </Typography>
            )}
          </Container>
          {/* Matching Format */}
          <MatchingFormatFormContainer>
            <MatchingHeadingContainer>
              <MatchingHeading>
                <Typography variant="h5">
                  <FormattedMessage id="project_editing_page.matching_format_setting.heading.title" />
                </Typography>
              </MatchingHeading>
              {availableMatchingFormats && availableMatchingFormats.length >= 2 && (
                <Typography variant="caption">
                  <FormattedMessage id="project_editing_page.matching_format_setting.heading.description" />
                </Typography>
              )}
            </MatchingHeadingContainer>

            {matchingFormatErrors &&
              matchingFormatErrors.length >= 1 &&
              matchingFormatErrors.map((mfErr) => (
                <div key={mfErr}>
                  <Typography variant="caption" color="error">
                    <FormattedMessage id={`project_editing_page.matching_format_setting.error.${mfErr}`} />
                  </Typography>
                </div>
              ))}
            <MatchingFormatContainer disabled={!isLoading && formatUnchangeable}>
              {isLoading || !availableMatchingFormats ? (
                <Spinner />
              ) : (
                <DueFormatsRadioGroup
                  inputtedMatchingFormat={state.matchingFormat}
                  onChangeMatchingFormat={(selected: MatchingFormat) => {
                    updateProjectMatchingFormat(selected);
                  }}
                  availableMatchingFormats={availableMatchingFormats}
                />
              )}
            </MatchingFormatContainer>
          </MatchingFormatFormContainer>
        </Grid>
      </Grid>

      {/*don't show on flat*/}
      {!isFlat && (
        <MobileOnlyWorkflowStepper
          step="due"
          updateDraftAndGoToTargetStep={(targetStep) =>
            updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'due', targetStep })
          }
        />
      )}
      {!isFlat && (
        <DueActions
          saveAndGoBack={() =>
            updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'due', targetStep: 'format' })
          }
          saveAndGoForward={() =>
            updateDraftProjectAndGoToDifferentTab({ state, currentStep: 'due', targetStep: 'brief' })
          }
          saveAndGoToProjectList={() => updateDraftProjectAndGoToProjectsList({ state, currentStep: 'due' })}
        />
      )}
    </Root>
  );
};

const Root = styled.section<{ isFlat: boolean }>`
  width: auto;
  max-width: ${SCREEN_LARGE}px;
  margin: ${(props) => (props.isFlat ? 0 : SPACING_HUGE)}px auto ${(props) => (props.isFlat ? 0 : SPACING_UNIT * 12)}px;
  padding-bottom: ${(props) => (props.isFlat ? SPACING_UNIT * 8 : SPACING_UNIT * 12)}px;

  ${mq('sp')} {
    margin-top: ${SPACING_REGULAR}px;
    padding-left: ${SPACING_REGULAR}px;
    padding-right: ${SPACING_REGULAR}px;
    padding-bottom: ${SPACING_UNIT * 6}px;
  }

,
`;

const Heading = styled(Typography)`
  margin-bottom: ${SPACING_LARGE}px;

  ${mq('sp')} {
    font-size: 1.3rem;
  }
`;

const MatchingFormatFormContainer = styled('div')`
  margin-top: ${SPACING_LARGE}px;
`;

const MatchingHeading = styled('div')`
  display: flex;
`;

const MatchingHeadingContainer = styled('div')`
  margin-bottom: ${SPACING_LARGE / 2}px;
`;

const Container = styled(Paper)`
  padding: ${SPACING_REGULAR}px;

  ${mq('tab')} {
    margin-bottom: ${SPACING_LARGE}px;
  }

  ${mq('sp')} {
    margin-bottom: ${SPACING_REGULAR}px;
  }
`;

const MatchingFormatContainer = styled(Paper)<{ disabled: boolean }>`
  padding: ${SPACING_REGULAR}px;
  pointer-events: ${(props) => props.disabled && 'none'};
  background-color: ${(props) => props.disabled && '#e8e8e8'};

  ${mq('tab')} {
    margin-bottom: ${SPACING_LARGE}px;
  }

  ${mq('sp')} {
    margin-bottom: ${SPACING_REGULAR}px;
  }
`;

export default DueScreen;
